import './style.scss'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
    FiUsers, FiFileText, FiBarChart2, FiSettings, FiPhone,
    FiGlobe, FiCalendar, FiHash, FiArrowRight, FiBriefcase, FiType, FiMail,
} from 'react-icons/fi'
import utility from 'src/utils/utility'
import {
    BackLink, Card, Text, Flex, Box, tokens,
} from 'src/componentv2'
import { fontBold, fontMedium, fontRegular } from 'src/model/v2/interface/constants'
import ConfigurationApiContext, { InstitutionDetail } from 'src/provider/API/v2/call-service/configuration'

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
    Active: { bg: '#ECFDF3', color: '#027A48' },
    Inactive: { bg: '#F2F4F7', color: '#667085' },
}

function StatusPill({ status }: { status: string }) {
    const s = STATUS_STYLES[status] || STATUS_STYLES.Inactive
    return (
        <span className="institution-details__status-pill" style={{ background: s.bg, color: s.color }}>
            {status}
        </span>
    )
}

function StatTile({
                      icon: Icon, label, value, iconBg, iconColor,
                  }: {
    icon: React.ElementType
    label: string
    value: string
    iconBg: string
    iconColor: string
}) {
    return (
        <Flex items="center" gap="1.2" padding="1.6">
            <Box
                width="3.6" height="3.6" radius="0.8"
                bgColor={iconBg}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            >
                <Icon size={16} color={iconColor} />
            </Box>
            <Flex direction="column" items="flex-start" gap="0.2" style={{ minWidth: 0 }}>
                <Text fontSize="1.2" color={tokens.textMuted} style={fontMedium}>
                    {label}
                </Text>
                <Text fontSize="1.4" style={fontMedium} color={tokens.textDark} className="institution-details__stat-value">
                    {value}
                </Text>
            </Flex>
        </Flex>
    )
}

function FeatureCard({
                         icon: Icon, iconBg, iconColor, title, description, onClick,
                     }: {
    icon: React.ElementType
    iconBg: string
    iconColor: string
    title: string
    description: string
    onClick: () => void
}) {
    return (
        <Card
            padding="2.4"
            radius="1.2"
            borderColor={tokens.border}
            shadow={tokens.shadowCard}
            className="institution-details__feature-card"
            onClick={onClick}
        >
            <Flex justify="space-between" items="flex-start">
                <Box
                    width="4" height="4" radius="0.8"
                    bgColor={iconBg}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <Icon size={18} color={iconColor} />
                </Box>
                <Box className="institution-details__feature-arrow">
                    <FiArrowRight size={16} color={tokens.primary} />
                </Box>
            </Flex>

            <Text topMargin="1.6" fontSize="1.6" style={fontMedium} color={tokens.textDark}>
                {title}
            </Text>
            <Text topMargin="0.6" fontSize="1.4" color={tokens.textMuted} style={fontRegular}>
                {description}
            </Text>
        </Card>
    )
}

const InstitutionDetailsPage = () => {
    const [searchParams] = useSearchParams()
    const id = searchParams.get('institutionId')
    const navigate = useNavigate()
    const { getInstitutionDetail } = useContext(ConfigurationApiContext)

    const [inst, setInst] = useState<InstitutionDetail | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [notFound, setNotFound] = useState(false)

    useEffect(() => {
        if (!id) return
        setIsLoading(true)
        setNotFound(false)

        getInstitutionDetail(Number(id))
            .then((data: InstitutionDetail | null) => {
                if (data) {
                    setInst(data)
                } else {
                    setNotFound(true)
                }
            })
            .finally(() => setIsLoading(false))
    }, [id])

    const handleBack = () => navigate('/dashboardv2/institution-list')

    if (isLoading) {
        return (
            <div className="institution-details" style={{ padding: '2.4rem 3.2rem' }}>
                <Text fontSize="1.4" color={tokens.textMuted}>Loading institution...</Text>
            </div>
        )
    }

    if (notFound || !inst) {
        return (
            <div className="institution-details" style={{ padding: '2.4rem 3.2rem' }}>
                <Flex items="center" gap="1.2">
                    <BackLink onClick={handleBack} color="#000000">{''}</BackLink>
                    <Text fontSize="2.4" style={fontBold} color={tokens.textDark}>
                        Institution Details
                    </Text>
                </Flex>
                <Text topMargin="1.6" fontSize="1.4" color={tokens.textMuted}>
                    Institution not found.
                </Text>
            </div>
        )
    }

    const features = [
        {
            icon: FiUsers,
            iconBg: '#EFF8FF',
            iconColor: '#1570EF',
            title: 'Customer Management',
            description: 'View and manage customers, CBA accounts, and account upgrade requests for this institution.',
            path: `/dashboardv2/institutions/${inst.institutionId}/customer-management`,
        },
        {
            icon: FiFileText,
            iconBg: '#F4F3FF',
            iconColor: '#6938EF',
            title: 'Audit Trail',
            description: 'Review system activity logs, user actions, and change history across this institution.',
            path: `/dashboardv2/institutions/${inst.institutionId}/audit-trail`,
        },
        {
            icon: FiBarChart2,
            iconBg: '#ECFDF3',
            iconColor: '#039855',
            title: 'Reports',
            description: 'Access performance reports, transaction summaries and analytics for this institution.',
            path: `/dashboardv2/institutions/${inst.institutionId}/reports`,
        },
        {
            icon: FiSettings,
            iconBg: '#FFFAEB',
            iconColor: '#B54708',
            title: 'Configuration',
            description: 'Manage institution profile, branding, business logic, app generation, and team settings.',
            path: `/dashboardv2/institution-configuration?institutionId=${inst.institutionId}&tab=profile`,
        },
    ]

    return (
        <div className="institution-details" style={{ padding: '2.4rem 3.2rem' }}>
                    <Flex items="center" gap="1.2">
                        <BackLink onClick={handleBack} color="#000000">{''}</BackLink>
                        <Text fontSize="2.4" style={fontBold} color={tokens.textDark}>
                            Institution Details
                        </Text>
                    </Flex>

                    <Card
                        topMargin="2.4"
                        radius="1.2"
                        borderColor={tokens.border}
                        shadow={tokens.shadowCard}
                        padding="0"
                        className="institution-details__summary-card"
                    >
                        <Flex
                            justify="space-between"
                            items="flex-start"
                            style={{ padding: '2rem 2.4rem', borderBottom: `1px solid ${tokens.border}` }}
                        >
                            <Flex items="flex-start" gap="1.2">
                                <Box
                                    width="4.8" height="4.8" radius="1.2"
                                    bgColor={tokens.primarySoft}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}
                                >
                                    {inst.logo ? (
                                        <img src={inst.logo} alt={inst.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <FiBriefcase size={22} color={tokens.primary} />
                                    )}
                                </Box>
                                <Flex direction="column" gap="0.4" items="flex-start">
                                    <Text fontSize="2.2" style={fontBold} color={tokens.textDark}>
                                        {inst.name}
                                    </Text>
                                    <Text fontSize="1.4" color={tokens.textMuted}>
                                        {inst.institutionCode ?? 'N/A'} &middot; {inst.institutionType ?? 'N/A'} &middot; {inst.isBaas ? 'BaaS' : 'Non-BaaS'}
                                    </Text>
                                </Flex>
                            </Flex>

                            <StatusPill status={inst.isActive ? 'Active' : 'Inactive'} />
                        </Flex>

                        <div className="institution-details__stats-grid">
                            <StatTile icon={FiUsers} label="Total Users" value={inst.totalUsers.toLocaleString()} iconBg="#EFF8FF" iconColor="#1570EF" />
                            <StatTile icon={FiType} label="Language" value={inst.language ?? 'N/A'} iconBg="#F4F3FF" iconColor="#6938EF" />
                            <StatTile icon={FiGlobe} label="Country" value={inst.country ?? 'N/A'} iconBg="#ECFDF3" iconColor="#039855" />
                            <StatTile icon={FiCalendar} label="Date Joined" value={inst.setupDate ?? 'N/A'} iconBg="#FFFAEB" iconColor="#B54708" />
                        </div>

                        <div className="institution-details__stats-grid institution-details__stats-grid--secondary">
                            <StatTile icon={FiHash} label="Institution Code" value={inst.institutionCode ?? 'N/A'} iconBg={tokens.pageBg} iconColor={tokens.textMuted} />
                            <StatTile icon={FiPhone} label="Phone" value={inst.correspondencePhone ?? 'N/A'} iconBg={tokens.pageBg} iconColor={tokens.textMuted} />
                            <StatTile icon={FiMail} label="Email" value={inst.correspondenceEmail ?? 'N/A'} iconBg={tokens.pageBg} iconColor={tokens.textMuted} />
                            <StatTile icon={FiBriefcase} label="Type" value={inst.institutionType ?? 'N/A'} iconBg={tokens.pageBg} iconColor={tokens.textMuted} />
                        </div>
                    </Card>

                    <Text topMargin="2.4" fontSize="1.6" style={fontMedium} color={tokens.textDark}>
                        Manage Institution
                    </Text>

                    <div className="institution-details__feature-grid">
                        {features.map((f) => (
                            <FeatureCard
                                key={f.title}
                                icon={f.icon}
                                iconBg={f.iconBg}
                                iconColor={f.iconColor}
                                title={f.title}
                                description={f.description}
                                onClick={() => navigate(f.path)}
                            />
                        ))}
                    </div>
        </div>
    )
}

export default utility.routeData(
    'institution-details',
    'Institution Details',
    <InstitutionDetailsPage />,
)